import { Empty, EmptyHeader, EmptyMedia, EmptyTitle } from "@choose/ui/empty";
import { Separator } from "@choose/ui/separator";
import { Spinner } from "@choose/ui/spinner";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnFiltersState } from "@tanstack/react-table";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  DEFAULT_COLUMN_VISIBILITY,
  TABLE_ACCESSOR_STATUS,
  TABLE_ACCESSOR_SUB_STATUS,
  TicketList,
} from "~/components/common/tickets/ticket-list";
import { TicketsDisplay } from "~/components/common/tickets/tickets-display";
import { TicketsFilters } from "~/components/common/tickets/tickets-filters";
import { APP_SIDEBAR_NAME } from "~/components/layout/sidebars/app-sidebar";
import { SidebarTrigger } from "~/components/ui/sidebar";
import {
  SupportTicketStatus,
  SupportTicketSubStatus,
} from "~/graphql/graphql.gen";
import { useTickets } from "~/hooks/use-tickets";
import { DEFAULT_FILTERS as TODO_DEFAULT_FILTERS } from "~/routes/_auth/_app_shell/_main-navigation/tickets/todo";
import { countFilters, useQueueStore } from "~/stores/queue-store";
import {
  queryParamsToFilters,
  queryParamsToTableColumnVisibility,
  queryParamsToTableFilters,
  queryParamsToTableGrouping,
  queryParamsToTableSorting,
  useTicketsStore,
} from "~/stores/tickets-store";
import { searchTicketsQueryParamsSchema } from "~/validators/search-tickets-validator";

export const DEFAULT_FILTERS: ColumnFiltersState = [
  {
    id: TABLE_ACCESSOR_STATUS,
    value: {
      operator: "is",
      value: [SupportTicketStatus.Todo],
    },
  },
  {
    id: TABLE_ACCESSOR_SUB_STATUS,
    value: {
      operator: "is",
      value: [SupportTicketSubStatus.NeedsFirstResponse],
    },
  },
];

export const Route = createFileRoute(
  "/_auth/_app_shell/_main-navigation/tickets/needs-first-response",
)({
  component: RouteComponent,
  validateSearch: (search) => searchTicketsQueryParamsSchema.parse(search),
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();

  const { data: tickets, isLoading } = useTickets();
  const filterTickets = useTicketsStore((state) => state.filterTickets);
  const sortTickets = useTicketsStore((state) => state.sortTickets);
  const { setQueue } = useQueueStore();

  const filters = queryParamsToTableFilters(search.filters, DEFAULT_FILTERS);
  const columnVisibility = queryParamsToTableColumnVisibility(
    search,
    DEFAULT_COLUMN_VISIBILITY,
  );
  const grouping = queryParamsToTableGrouping(search);
  const sorting = queryParamsToTableSorting(search);

  useEffect(() => {
    setQueue({
      display: {
        filterCount: countFilters({
          currentFilters: filters,
          referenceFilters: TODO_DEFAULT_FILTERS,
        }),
        subTitle: t("ticket.status.todo"),
        title: t("app_sidebar.tickets"),
      },
      root: {
        page: {
          to: Route.fullPath,
          search,
        },
      },
      state: {
        columnFilters: filters,
        sorting,
      },
    });
  }, [filters, search, sorting, setQueue, t]);

  const ticketFilters = queryParamsToFilters(search.filters);
  const filteredTickets = sortTickets(filterTickets(tickets, filters), sorting);

  return (
    <>
      <title>{`${t("ticket.sub_status.needs_first_response")} - ${t("app_name")}`}</title>
      <div className="bg-background flex w-full flex-col md:rounded-md md:border">
        <div className="flex h-full grow flex-col">
          <header className="flex flex-col items-center">
            <div className="flex h-10 w-full items-center gap-2 border-b px-4">
              <SidebarTrigger name={APP_SIDEBAR_NAME} className="-ml-1" />
              <Separator orientation="vertical" className="mr-2" />

              <h2 className="text-sm">
                {t("ticket.sub_status.needs_first_response")}
              </h2>
            </div>
            <div className="flex min-h-10 w-full items-center justify-between gap-2 border-b px-4 py-0.75 pl-5.5">
              <div className="flex flex-wrap items-center gap-2">
                <TicketsFilters
                  activeFilters={{
                    subStatus: [],
                  }}
                  onFiltersChange={(filters) =>
                    void navigate({ search: (prev) => ({ ...prev, filters }) })
                  }
                  filters={ticketFilters}
                />
              </div>
              <TicketsDisplay
                onDisplayChange={(update) => {
                  void navigate({
                    search: (prev) => ({ ...prev, ...update(prev) }),
                  });
                }}
                display={search}
              />
            </div>
          </header>
          <div className="h-full w-full overflow-auto">
            {isLoading ? (
              <div className="flex h-full w-full items-center justify-center">
                <Empty className="min-h-[300px] w-full max-w-md">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <Spinner className="size-4" />
                    </EmptyMedia>
                    <EmptyTitle>{t("tickets.loading")}</EmptyTitle>
                  </EmptyHeader>
                </Empty>
              </div>
            ) : (
              <TicketList
                data={filteredTickets}
                hasFilters={Object.keys(ticketFilters).length > 0}
                state={{
                  columnFilters: filters,
                  columnVisibility: columnVisibility,
                  grouping: grouping,
                  sorting: sorting,
                }}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
